/**
 * Ticket System - Realtime Database Helpers
 * 
 * Firestore → Realtime Database 移行用のヘルパー関数
 * 環境パス管理、データ変換、クエリユーティリティ
 */

import { realtimeDatabaseEnvPath } from './firebaseConfig_tickets';

/**
 * 環境パスを取得（dev または prd）
 */
export const getEnvPath = () => realtimeDatabaseEnvPath;

/**
 * Realtime Database パスを生成
 * @param {string} path - 相対パス（例: 'tickets/ticket123'）
 * @returns {string} - 環境パス付きの完全パス（例: 'dev/tickets/ticket123'）
 */
export const createRtdbPath = (path) => {
  const envPath = getEnvPath();
  const cleanPath = path.startsWith('/') ? path.slice(1) : path;
  return `${envPath}/${cleanPath}`;
};

/**
 * Tickets コレクションのパスを生成
 * @param {string} ticketId - チケットID（省略可）
 * @returns {string} - Realtime Database パス
 */
export const getTicketsPath = (ticketId = null) => {
  const basePath = createRtdbPath('tickets');
  return ticketId ? `${basePath}/${ticketId}` : basePath;
};

/**
 * QR Chunks サブコレクションのパスを生成
 * @param {string} ticketId - チケットID
 * @param {string} chunkId - チャンクID（省略可）
 * @returns {string} - Realtime Database パス
 */
export const getQRChunksPath = (ticketId, chunkId = null) => {
  const basePath = `${getTicketsPath(ticketId)}/qr_chunks`;
  return chunkId ? `${basePath}/${chunkId}` : basePath;
};

/**
 * QR Index サブコレクションのパスを生成
 * @param {string} ticketId - チケットID
 * @param {string} qrCode - QRコード（省略可）
 * @returns {string} - Realtime Database パス
 */
export const getQRIndexPath = (ticketId, qrCode = null) => {
  const basePath = `${getTicketsPath(ticketId)}/qr_index`;
  if (!qrCode) return basePath;
  
  // QRコードをエンコード（Realtime Databaseのキー制約対応）
  const encodedQRCode = encodeURIComponent(String(qrCode));
  return `${basePath}/${encodedQRCode}`;
};

/**
 * QR Codes（レガシー）サブコレクションのパスを生成
 * @param {string} ticketId - チケットID
 * @param {string} qrCodeId - QRコードID（省略可）
 * @returns {string} - Realtime Database パス
 */
export const getQRCodesPath = (ticketId, qrCodeId = null) => {
  const basePath = `${getTicketsPath(ticketId)}/qr_codes`;
  return qrCodeId ? `${basePath}/${qrCodeId}` : basePath;
};

/**
 * Permissions サブコレクションのパスを生成
 * @param {string} ticketId - チケットID
 * @param {string} permissionId - 権限ID（省略可）
 * @returns {string} - Realtime Database パス
 */
export const getPermissionsPath = (ticketId, permissionId = null) => {
  const basePath = `${getTicketsPath(ticketId)}/permissions`;
  return permissionId ? `${basePath}/${permissionId}` : basePath;
};

/**
 * ユーザーのcanonical ID → Firebase UID マッピングパスを生成
 * ゲストから別アカウントに移行した際のチケット所有権確認に使用
 * @param {string} canonicalUserId - canonical ユーザーID
 * @param {string} firebaseUid - Firebase UID（省略可）
 * @returns {string} - Realtime Database パス
 */
export const getUserCanonicalLinksPath = (canonicalUserId, firebaseUid = null) => {
  const basePath = createRtdbPath(`user_canonical_links/${canonicalUserId}`);
  return firebaseUid ? `${basePath}/${firebaseUid}` : basePath;
};

/**
 * Firestore Timestamp → Unix timestamp (ms) 変換
 * @param {any} timestamp - Firestore Timestamp または Date または数値
 * @returns {number|null} - Unix timestamp (ms) または null
 */
export const convertToUnixTimestamp = (timestamp) => {
  if (!timestamp) return null;
  
  // 既に数値の場合
  if (typeof timestamp === 'number') return timestamp;
  
  // Firestore Timestamp の場合
  if (timestamp?.toMillis) return timestamp.toMillis();
  
  // Date オブジェクトの場合
  if (timestamp instanceof Date) return timestamp.getTime();
  
  // ISO文字列の場合
  if (typeof timestamp === 'string') {
    const date = new Date(timestamp);
    return isNaN(date.getTime()) ? null : date.getTime();
  }
  
  return null;
};

/**
 * Unix timestamp (ms) → Date オブジェクト変換
 * @param {number|null} timestamp - Unix timestamp (ms)
 * @returns {Date|null} - Date オブジェクト または null
 */
export const convertFromUnixTimestamp = (timestamp) => {
  if (!timestamp || typeof timestamp !== 'number') return null;
  return new Date(timestamp);
};

/**
 * Firestore データを Realtime Database 形式に変換
 * - Timestamp → Unix timestamp (ms)
 * - undefined → null
 * @param {object} data - Firestore データ
 * @returns {object} - Realtime Database 形式のデータ
 */
export const convertFirestoreToRtdb = (data) => {
  if (!data || typeof data !== 'object') return data;
  
  const converted = {};
  
  for (const [key, value] of Object.entries(data)) {
    // undefined は null に変換（Realtime Database は undefined 非対応）
    if (value === undefined) {
      converted[key] = null;
      continue;
    }
    
    // Timestamp 変換
    if (value?.toMillis) {
      converted[key] = value.toMillis();
      continue;
    }
    
    // Date オブジェクト変換
    if (value instanceof Date) {
      converted[key] = value.getTime();
      continue;
    }
    
    // ネストされたオブジェクト
    if (value && typeof value === 'object' && !Array.isArray(value)) {
      converted[key] = convertFirestoreToRtdb(value);
      continue;
    }
    
    // 配列
    if (Array.isArray(value)) {
      converted[key] = value.map(item => 
        item && typeof item === 'object' ? convertFirestoreToRtdb(item) : item
      );
      continue;
    }
    
    // その他
    converted[key] = value;
  }
  
  return converted;
};

/**
 * Realtime Database データを Firestore 互換形式に変換
 * - Unix timestamp (ms) → Date オブジェクト（特定フィールドのみ）
 * @param {object} data - Realtime Database データ
 * @param {string[]} timestampFields - タイムスタンプとして扱うフィールド名
 * @returns {object} - Firestore 互換形式のデータ
 */
export const convertRtdbToFirestore = (data, timestampFields = ['createdAt', 'updatedAt', 'enteredAt', 'lastScanAt', 'expiresAt', 'deleteAt']) => {
  if (!data || typeof data !== 'object') return data;
  
  const converted = { ...data };
  
  for (const field of timestampFields) {
    if (converted[field] && typeof converted[field] === 'number') {
      converted[field] = new Date(converted[field]);
    }
  }
  
  return converted;
};

/**
 * Realtime Database スナップショットを配列に変換
 * @param {DataSnapshot} snapshot - Realtime Database スナップショット
 * @returns {Array} - { id, ...data } 形式の配列
 */
export const snapshotToArray = (snapshot) => {
  const result = [];
  
  if (!snapshot || !snapshot.exists()) return result;
  
  snapshot.forEach((childSnapshot) => {
    result.push({
      id: childSnapshot.key,
      ...childSnapshot.val()
    });
  });
  
  return result;
};

/**
 * Realtime Database の複数パス更新用オブジェクトを生成
 * @param {object} updates - { path: value } 形式のオブジェクト
 * @returns {object} - Realtime Database update() 用のオブジェクト
 */
export const createMultiPathUpdates = (updates) => {
  const result = {};
  
  for (const [path, value] of Object.entries(updates)) {
    const fullPath = path.startsWith('/') ? path.slice(1) : path;
    result[fullPath] = value;
  }
  
  return result;
};

/**
 * QRコードをエンコード（Realtime Database キー用）
 * Realtime Database のキーには . $ # [ ] / が使えないため
 * @param {string} qrCode - QRコード
 * @returns {string} - エンコードされたQRコード
 */
export const encodeQRCode = (qrCode) => {
  return encodeURIComponent(String(qrCode || ''));
};

/**
 * QRコードをデコード
 * @param {string} encodedQRCode - エンコードされたQRコード
 * @returns {string} - デコードされたQRコード
 */
export const decodeQRCode = (encodedQRCode) => {
  return decodeURIComponent(String(encodedQRCode || ''));
};

/**
 * Realtime Database のクエリ結果をフィルタリング
 * orderByChild + equalTo の結果をさらに絞り込む場合に使用
 * @param {Array} items - クエリ結果の配列
 * @param {object} filters - { field: value } 形式のフィルター
 * @returns {Array} - フィルタリングされた配列
 */
export const filterQueryResults = (items, filters) => {
  if (!Array.isArray(items) || !filters) return items;
  
  return items.filter(item => {
    for (const [field, value] of Object.entries(filters)) {
      if (item[field] !== value) return false;
    }
    return true;
  });
};

/**
 * エラーハンドリング: Realtime Database エラーを判定
 * @param {Error} error - エラーオブジェクト
 * @returns {boolean} - Realtime Database エラーかどうか
 */
export const isRtdbError = (error) => {
  const code = error?.code || '';
  return code.startsWith('DATABASE/') || 
         code === 'PERMISSION_DENIED' ||
         code === 'NETWORK_ERROR';
};

/**
 * デバッグ用: パス情報を出力
 */
export const debugPaths = () => {
  console.log('[RTDB Helpers] Environment:', getEnvPath());
  console.log('[RTDB Helpers] Tickets path:', getTicketsPath());
  console.log('[RTDB Helpers] Sample ticket path:', getTicketsPath('ticket123'));
  console.log('[RTDB Helpers] Sample QR chunks path:', getQRChunksPath('ticket123'));
  console.log('[RTDB Helpers] Sample QR index path:', getQRIndexPath('ticket123', 'QR001'));
};
